<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import V4Button from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import ReportHeader from '../../../reports/components/ReportHeader.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ReportChart from '../../components/ReportChart.vue';
import CustomReportDrilldownDrawer from '../../components/CustomReportDrilldownDrawer.vue';
import PeriodRangePicker from '../dashboards/components/PeriodRangePicker.vue';
import {
  buildQueryParamsFor,
  buildChartData,
  buildChartProps,
  hasChartData,
  formatMetricValue,
  formatTableCell,
  buildTableCellTotals,
  formatPeriodLabel,
  buildDimensionLabel,
  buildDrilldownParams,
  describeDrilldownBucket,
  drilldownBucketForClick,
  drilldownBucketForRow,
} from '../../helpers/reportChartHelpers';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const GROUP_BY_OPTIONS = computed(() => [
  { value: 'day', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.DAY') },
  { value: 'week', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.WEEK') },
  { value: 'month', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.MONTH') },
  { value: 'year', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.YEAR') },
  { value: 'none', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.NONE') },
]);

const VALUE_MODE_OPTIONS = computed(() => [
  {
    value: 'absolute',
    label: t('WGPT_CUSTOM_REPORTS.VALUE_MODES.ABSOLUTE'),
  },
  {
    value: 'percentage',
    label: t('WGPT_CUSTOM_REPORTS.VALUE_MODES.PERCENTAGE'),
  },
]);

const selectedGroupBy = ref('day');
const selectedDimension = ref('');
const selectedValueMode = ref('absolute');
// The resolved absolute range emitted by PeriodRangePicker (set on its mount).
const selectedPeriod = ref(null);
const activeTab = ref('timeseries');
// The params of the query on screen, so a drilldown lists exactly the events it aggregated.
const lastQueryParams = ref({});
// { params, bucketLabel, bucketValue } of the open drilldown, null while closed.
const drilldown = ref(null);

// Suppresses the control watchers and the picker's mount-time emit while a report
// is being loaded and its defaults applied, so opening or switching a report runs
// exactly one query — with the new report's own defaults, never the previous
// report's dimension.
const isInitialising = ref(true);

const report = useMapGetter('wgptCustomReports/getActiveReport');
const queryResult = useMapGetter('wgptCustomReports/getQueryResult');
const uiFlags = useMapGetter('wgptCustomReports/getUIFlags');

const reportId = computed(() => route.params.reportId);

const metrics = computed(() => report.value?.config?.metrics || []);
const dimensions = computed(() => report.value?.config?.dimensions || []);
const dimensionOptions = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_REPORTS.VIEWER.SUMMARY') },
  ...dimensions.value.map(dim => ({
    value: dim.property || dim.source,
    label: dim.label || dim.property || dim.source,
  })),
]);
const defaultVisualization = computed(
  () => report.value?.config?.default_visualization || 'bar_chart'
);

const timeseriesData = computed(() => queryResult.value?.timeseries || []);
const summaryData = computed(() => queryResult.value?.summary || {});
const totalsData = computed(() => queryResult.value?.totals || {});
const showSummaryCards = computed(
  () => report.value?.config?.show_summary_cards ?? false
);

const hasDimensionData = computed(
  () =>
    !!selectedDimension.value &&
    timeseriesData.value[0]?.dimension_value !== undefined
);

const dimensionLabel = computed(() =>
  buildDimensionLabel(
    queryResult.value?.dimension_labels,
    t('WGPT_CUSTOM_REPORTS.VIEWER.NO_VALUE')
  )
);

const isNoTimeAggregation = computed(() => selectedGroupBy.value === 'none');

// Denominators for percentage table cells, matching the chart's normalisation.
const tableCellTotal = computed(() =>
  buildTableCellTotals(
    timeseriesData.value,
    metrics.value,
    hasDimensionData.value && !isNoTimeAggregation.value
  )
);

const isTable = computed(() => defaultVisualization.value === 'table');

const chartData = computed(() =>
  buildChartData({
    timeseries: timeseriesData.value,
    metrics: metrics.value,
    granularity: selectedGroupBy.value,
    hasDimension: hasDimensionData.value,
    visualization: defaultVisualization.value,
    valueMode: selectedValueMode.value,
    dimensionLabel: dimensionLabel.value,
  })
);

const chartProps = computed(() =>
  buildChartProps({
    valueMode: selectedValueMode.value,
    hasDimension: hasDimensionData.value,
    granularity: selectedGroupBy.value,
    metrics: metrics.value,
  })
);

const hasChart = computed(() => hasChartData(chartData.value));

const tableColumns = computed(() => {
  const cols = [];
  if (!isNoTimeAggregation.value) {
    cols.push({
      key: '__period',
      label: t('WGPT_CUSTOM_REPORTS.VIEWER.PERIOD'),
    });
  }
  if (hasDimensionData.value) {
    cols.push({ key: '__dimension', label: selectedDimension.value });
  }
  metrics.value.forEach(metric => {
    cols.push({ key: metric.key, label: metric.label || metric.key });
  });
  return cols;
});

async function fetchReport() {
  try {
    await store.dispatch('wgptCustomReports/show', reportId.value);
  } catch {
    useAlert(t('WGPT_CUSTOM_REPORTS.VIEWER.ERROR'));
  }
}

async function fetchData() {
  const params = buildQueryParamsFor({
    granularity: selectedGroupBy.value,
    dimension: selectedDimension.value,
    since: selectedPeriod.value?.since,
    until: selectedPeriod.value?.until,
  });
  try {
    await store.dispatch('wgptCustomReports/query', {
      reportId: reportId.value,
      params,
    });
    lastQueryParams.value = params;
  } catch {
    useAlert(t('WGPT_CUSTOM_REPORTS.VIEWER.ERROR'));
  }
}

function openDrilldown(bucket, bucketValue = '') {
  drilldown.value = {
    params: buildDrilldownParams(lastQueryParams.value, bucket),
    bucketLabel: describeDrilldownBucket(
      bucket,
      selectedGroupBy.value,
      dimensionLabel.value
    ),
    bucketValue,
  };
}

function onChartItemClick(click) {
  openDrilldown(
    drilldownBucketForClick(chartData.value, click),
    click.formattedValue
  );
}

function onTimeseriesRowClick(row) {
  openDrilldown(
    drilldownBucketForRow(row, {
      timeBucketed: !isNoTimeAggregation.value,
      hasDimension: hasDimensionData.value,
    })
  );
}

function onSummaryRowClick(row) {
  openDrilldown(
    drilldownBucketForRow(row, { timeBucketed: false, hasDimension: true })
  );
}

function navigateToEdit() {
  router.push(
    accountScopedRoute('wgpt_reports_settings_edit', {
      reportId: reportId.value,
    })
  );
}

function onPeriodChange(range) {
  selectedPeriod.value = range;
  if (!isInitialising.value) fetchData();
}

async function loadReport() {
  isInitialising.value = true;
  activeTab.value = 'timeseries';
  drilldown.value = null;
  await fetchReport();
  // Initialise the controls from the report's configured defaults so opening a
  // report reflects how it was set up (granularity, breakdown, value format).
  const config = report.value?.config || {};
  selectedGroupBy.value = config.default_time_granularity || 'day';
  selectedDimension.value = config.default_group_by || '';
  selectedValueMode.value = config.default_value_mode || 'absolute';
  isInitialising.value = false;
  fetchData();
}

onMounted(() => {
  loadReport();
});

watch(reportId, () => {
  loadReport();
});

// selectedValueMode is deliberately absent: percentage/absolute is applied
// client-side, so flipping it needs no requery.
watch([selectedGroupBy, selectedDimension], () => {
  if (isInitialising.value) return;
  fetchData();
});
</script>

<template>
  <div>
    <ReportHeader
      :header-title="report.name || t('WGPT_CUSTOM_REPORTS.HEADER')"
      has-back-button
    >
      <V4Button
        icon="i-lucide-pencil"
        variant="ghost"
        size="sm"
        @click="navigateToEdit"
      />
    </ReportHeader>

    <p v-if="report.description" class="text-sm text-n-slate-11 mb-4">
      {{ report.description }}
    </p>

    <div class="flex flex-wrap items-center gap-3 mb-6">
      <Select v-model="selectedGroupBy" :options="GROUP_BY_OPTIONS" />
      <Select
        v-if="dimensions.length"
        v-model="selectedDimension"
        :options="dimensionOptions"
      />
      <Select v-model="selectedValueMode" :options="VALUE_MODE_OPTIONS" />
      <!-- Period filter is kept at the end so the custom date fields can appear
           without shifting the controls above. Keyed by report so switching
           reports resets the range to its default. -->
      <PeriodRangePicker :key="reportId" @change="onPeriodChange" />
      <V4Button
        icon="i-lucide-refresh-cw"
        variant="ghost"
        size="sm"
        :label="t('WGPT_CUSTOM_REPORTS.VIEWER.REFRESH')"
        @click="fetchData"
      />
    </div>

    <div
      v-if="uiFlags.isQuerying || uiFlags.isFetchingItem"
      class="flex items-center justify-center py-16"
    >
      <Spinner />
    </div>

    <template v-else>
      <div
        v-if="showSummaryCards && metrics.length"
        class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6"
      >
        <div
          v-for="metric in metrics"
          :key="metric.key"
          class="bg-n-surface-2 rounded-lg p-4"
        >
          <p class="text-sm text-n-slate-11 mb-1 truncate">
            {{ metric.label || metric.name || metric.key }}
          </p>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ formatMetricValue(totalsData[metric.key]) }}
          </p>
        </div>
      </div>

      <div class="flex border-b border-n-strong mb-6">
        <button
          class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
          :class="
            activeTab === 'timeseries'
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'timeseries'"
        >
          {{ t('WGPT_CUSTOM_REPORTS.VIEWER.TIMESERIES') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
          :class="
            activeTab === 'summary'
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'summary'"
        >
          {{ t('WGPT_CUSTOM_REPORTS.VIEWER.SUMMARY') }}
        </button>
      </div>

      <div v-if="activeTab === 'timeseries'">
        <div
          v-if="!timeseriesData.length"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div class="i-lucide-bar-chart-3 text-4xl text-n-slate-9 mb-4" />
          <p class="text-sm text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.VIEWER.NO_DATA') }}
          </p>
        </div>
        <div
          v-else-if="isTable"
          class="overflow-x-auto bg-n-surface-2 rounded-lg p-4"
        >
          <table class="w-full">
            <thead>
              <tr class="border-b border-n-strong">
                <th
                  v-for="col in tableColumns"
                  :key="col.key"
                  class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
                  :class="col.key.startsWith('__') ? '' : 'text-right'"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in timeseriesData"
                :key="idx"
                class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                tabindex="0"
                @click="onTimeseriesRowClick(row)"
                @keydown.enter.prevent="onTimeseriesRowClick(row)"
              >
                <td
                  v-for="col in tableColumns"
                  :key="col.key"
                  class="py-2 px-3 text-sm text-n-slate-12"
                  :class="col.key.startsWith('__') ? '' : 'text-right'"
                >
                  <template v-if="col.key === '__period'">
                    {{ formatPeriodLabel(row.period, selectedGroupBy) }}
                  </template>
                  <template v-else-if="col.key === '__dimension'">
                    {{ dimensionLabel(row.dimension_value) }}
                  </template>
                  <template v-else>
                    {{
                      formatTableCell(
                        row[col.key],
                        selectedValueMode,
                        tableCellTotal(row, col.key)
                      )
                    }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="hasChart" class="bg-n-surface-2 rounded-lg p-4">
          <ReportChart
            :visualization="defaultVisualization"
            :data="chartData"
            :aria-label="report.name || t('WGPT_CUSTOM_REPORTS.HEADER')"
            :height="320"
            :timeseries="!isNoTimeAggregation"
            drillable
            v-bind="chartProps"
            @item-click="onChartItemClick"
          />
        </div>
      </div>

      <div v-if="activeTab === 'summary'">
        <div
          v-if="!selectedDimension && !Array.isArray(summaryData)"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="bg-n-surface-2 rounded-lg p-4"
          >
            <p class="text-sm text-n-slate-11 mb-1">
              {{ metric.label || metric.name || metric.key }}
            </p>
            <p class="text-2xl font-semibold text-n-slate-12">
              {{ formatMetricValue(summaryData[metric.key]) }}
            </p>
          </div>
        </div>

        <div
          v-else-if="Array.isArray(summaryData) && summaryData.length"
          class="overflow-x-auto"
        >
          <table class="w-full">
            <thead>
              <tr class="border-b border-n-strong">
                <th
                  class="text-left py-3 px-4 text-sm font-medium text-n-slate-11"
                >
                  {{ selectedDimension }}
                </th>
                <th
                  v-for="metric in metrics"
                  :key="metric.key"
                  class="text-right py-3 px-4 text-sm font-medium text-n-slate-11"
                >
                  {{ metric.label || metric.name || metric.key }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in summaryData"
                :key="idx"
                class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                tabindex="0"
                @click="onSummaryRowClick(row)"
                @keydown.enter.prevent="onSummaryRowClick(row)"
              >
                <td class="py-3 px-4 text-sm text-n-slate-12">
                  {{ dimensionLabel(row.dimension_value) }}
                </td>
                <td
                  v-for="metric in metrics"
                  :key="metric.key"
                  class="py-3 px-4 text-sm text-n-slate-12 text-right"
                >
                  {{ formatMetricValue(row[metric.key]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <p class="text-sm text-n-slate-11">
            {{ t('WGPT_CUSTOM_REPORTS.VIEWER.NO_DATA') }}
          </p>
        </div>
      </div>

      <div class="mt-8 pt-6 border-t border-n-weak">
        <div class="flex flex-wrap gap-4 text-xs text-n-slate-11">
          <span>
            {{ t('WGPT_CUSTOM_REPORTS.VIEWER.EVENT_LABEL') }}
            <span class="font-medium text-n-slate-12">
              {{ report.event_definition_name }}
            </span>
          </span>
          <span v-for="metric in metrics" :key="metric.key">
            {{
              t('WGPT_CUSTOM_REPORTS.VIEWER.METRIC_LABEL', {
                metric: metric.label || metric.key,
              })
            }}
            <span class="font-medium text-n-slate-12 uppercase">
              {{ metric.aggregation }}
            </span>
          </span>
        </div>
      </div>
    </template>

    <CustomReportDrilldownDrawer
      :open="!!drilldown"
      :report-id="reportId"
      :title="report.name"
      :bucket-label="drilldown?.bucketLabel"
      :bucket-value="drilldown?.bucketValue"
      :params="drilldown?.params"
      @close="drilldown = null"
    />
  </div>
</template>
